import { ErrorFallback } from "@/components/ErrorFallback";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Text } from "@/components/podkit/typography/Text";
import { NewSecretModal } from "@/components/secrets/NewSecretModal";
import { SecretsCard } from "@/components/secrets/SecretsCard";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useListUserSecrets, type PlainSecret } from "@/queries/secret-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";
import { useCallback, useMemo, useState, type FC } from "react";

export const UserSecretsPage: FC = () => {
    useDocumentTitle("Secrets");

    const { data: secrets, isPending, error } = useListUserSecrets();
    const { data: user, isPending: isUserPending, error: userError } = useAuthenticatedUser();

    const [showAddSecretModal, setShowAddSecretModal] = useState(false);

    const onNewSecret = useCallback(() => {
        setShowAddSecretModal(true);
    }, [setShowAddSecretModal]);

    const content = useMemo(() => {
        if (error) {
            return <ErrorFallback error={error} />;
        }

        if (isPending || !user) {
            return (
                <SkeletonBlock
                    ready={!isPending && !isUserPending}
                    failed={!!error || !!userError}
                    className="h-[160px] w-[800px]"
                />
            );
        }

        const scope = new SecretScope({ scope: { case: "userId", value: user?.id } });
        return <UserSecrets scope={scope} secrets={secrets?.secrets ?? []} onNewSecret={onNewSecret} />;
    }, [error, isPending, isUserPending, user, secrets, onNewSecret, userError]);

    return (
        <div data-testid="user-secrets-page">
            {content}
            {showAddSecretModal && (
                <NewSecretModal
                    enableSecretContainerRegistry={true}
                    scope={new SecretScope({ scope: { case: "userId", value: user!.id } })}
                    onClose={() => setShowAddSecretModal(false)}
                />
            )}
        </div>
    );
};

const UserSecrets: FC<{ scope: SecretScope; secrets: PlainSecret[]; onNewSecret: () => void }> = ({
    scope,
    secrets,
    onNewSecret,
}) => {
    return (
        <div className="flex flex-col gap-6">
            <Text className="text-base text-content-secondary">
                These are your personal secrets that are available in all environments you create.
            </Text>
            <SecretsCard scope={scope} secrets={secrets} showCreatorColumn={false} onNewSecret={onNewSecret} />
        </div>
    );
};
